import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSClasses = () => {
    const classFundamentalsCode = `class ComputeEngine {
    // 1. Public Fields (ES2022)
    region = 'us-east-1';

    // 2. Private Fields (#) - Truly encapsulated
    #status = 'OFFLINE';

    constructor(id) {
        this.id = id;
    }

    // 3. Instance Method
    start() {
        this.#status = 'RUNNING';
        console.log(\`Engine \${this.id} is \${this.#status}\`);
    }

    // 4. Getter/Setter
    get status() { return this.#status; }
}`;

    const staticMechanicsCode = `class Database {
    static connectionLimit = 100;

    // ES2022 Static Initialization Block
    static {
        // Complex logic to run once when class is loaded
        try {
            const config = { env: 'PROD' };
            this.mode = config.env === 'PROD' ? 'SECURE' : 'DEV';
        } catch (e) {
            this.mode = 'SAFE';
        }
    }

    static connect() {
        console.log(\`Connecting in \${this.mode} mode...\`);
    }
}`;

    const inheritanceCode = `class BaseAPI {
    constructor(endpoint) {
        this.endpoint = endpoint;
    }
    fetch() { console.log('Base Fetch'); }
}

class UserAPI extends BaseAPI {
    constructor(endpoint, token) {
        // Must call super() before accessing 'this'
        super(endpoint);
        this.token = token;
    }

    // Overriding while calling parent
    fetch() {
        super.fetch();
        console.log('User Auth Header Applied');
    }
}`;

    const performanceCode = `// --- Performance Perspective ---

class HeavyObject {
    //  Anti-pattern: Creating methods inside constructor
    constructor() {
        this.log = () => console.log('Inefficient');
    }

    // Best practice: Method on prototype
    log() {
        console.log('Efficient');
    }
}

/* 
   Note: Field initializers (like click = () => {}) 
   are added to the instance, not the prototype. 
   Use them only when context binding is critical (e.g., React handlers).
*/`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">OOP Architecture</div>
                <h1>Modern ES6+ Classes</h1>
                <p className="hero-lead">
                    While JavaScript remains prototypal under the hood, <strong>Classes</strong>
                    provide a structured, readable syntax for implementing Object-Oriented
                    patterns. Modern updates (ES2022) have added true private fields
                    and static initialization blocks, making them production-ready
                    for enterprise architecture.
                </p>
            </div>

            <div className="content-section">
                <h2>Encapsulation and Fields</h2>
                <p>
                    Modern JavaScript classes support <strong>Public</strong> and
                    <strong>Private</strong> fields. Unlike the old <code>_variable</code>
                    convention, fields prefixed with <code>#</code> are truly private
                    at the engine level and cannot be accessed from outside the class
                    body, providing real data integrity.
                </p>
                <CodeBlock language="javascript" code={classFundamentalsCode} />
            </div>

            <div className="content-section">
                <h2>Static Initialization</h2>
                <p>
                    Static methods and properties belong to the class itself, not its
                    instances. ES2022 introduced <strong>Static Blocks</strong>,
                    allowing for complex logic (like <code>try/catch</code>) when
                    the class is first loaded by the engine.
                </p>
                <CodeBlock language="javascript" code={staticMechanicsCode} />
            </div>

            <div className="content-section">
                <h2>The Super Mechanism</h2>
                <p>
                    Inheritance allows a class to share behavior from a parent.
                    The <code>extends</code> keyword sets up the prototype chain,
                    and <code>super()</code> calls the parent constructor. Crucially,
                    you cannot use <code>this</code> in a child constructor until
                    <code>super()</code> has been called.
                </p>
                <CodeBlock language="javascript" code={inheritanceCode} />
            </div>

            <div className="content-section">
                <h2>Performance Considerations</h2>
                <p>
                    Under the hood, class methods are added to the <code>.prototype</code>.
                    This makes them highly memory efficient as thousands of instances
                    can share a single function reference. However, using arrow
                    functions as fields creates a new function for every instance.
                </p>
                <CodeBlock language="javascript" code={performanceCode} />
                <ProTip><strong> Performance Tip:</strong> Prefer standard methods
                    over arrow functions in classes unless you specifically need
                    to preserve <code>this</code> context for event listeners.</ProTip>
            </div>

            <KeyFacts><ul>
                    <li><strong>Encapsulation:</strong> Use <code>#private</code> fields for security-sensitive data.</li>
                    <li><strong>Inheritance:</strong> Use <code>super</code> to extend parent behaviors rather than rewriting them.</li>
                    <li><strong>Static Blocks:</strong> Use static blocks for complex configuration validation on startup.</li>
                    <li> <strong>Hoisting:</strong> Remember that classes are <strong>not hoisted</strong>; you must define them before usage.</li>
                    <li><strong>Strictness:</strong> Class bodies are always in <code>"use strict"</code> mode by default.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSClasses;


